import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaFacebook, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { IMAGES, SOCIAL_LINKS } from '../assets/constant';
import HeaderAnnouncementTicker from './HeaderAnnouncementTicker';

const socialWrap =
  'flex h-10 w-10 items-center justify-center rounded-full text-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0';

const SOCIAL_STYLES = {
  facebook: 'bg-[#1877F2] hover:bg-[#166FE5] ring-2 ring-white/30',
  linkedin: 'bg-[#0A66C2] hover:bg-[#095195] ring-2 ring-white/30',
  instagram:
    'bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:brightness-105 ring-2 ring-white/25',
};

function SocialLinks({ className = '' }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <a
        href={SOCIAL_LINKS.facebook}
        target="_blank"
        rel="noopener noreferrer"
        className={`${socialWrap} ${SOCIAL_STYLES.facebook}`}
        aria-label="Grover Sweets on Facebook"
      >
        <FaFacebook className="h-5 w-5" />
      </a>
      <a
        href={SOCIAL_LINKS.linkedin}
        target="_blank"
        rel="noopener noreferrer"
        className={`${socialWrap} ${SOCIAL_STYLES.linkedin}`}
        aria-label="Grover Sweets on LinkedIn"
      >
        <FaLinkedinIn className="h-5 w-5" />
      </a>
      <a
        href={SOCIAL_LINKS.instagram}
        target="_blank"
        rel="noopener noreferrer"
        className={`${socialWrap} ${SOCIAL_STYLES.instagram}`}
        aria-label="Grover Sweets on Instagram"
      >
        <FaInstagram className="h-5 w-5" />
      </a>
    </div>
  );
}

function Header() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const dashboardPath = '/dashboard';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinkClass =
    'rounded-lg px-1 py-2.5 text-base text-gray-600 transition hover:bg-gray-50 hover:text-brand';

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between py-3">
            <Link to="/" className="flex shrink-0 items-center gap-2 text-brand">
              <img
                src={IMAGES.LOGO}
                alt="Grover Sweets Logo"
                className="h-10 w-auto object-contain sm:h-12"
              />
            </Link>

            <nav className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 transform items-center gap-6 text-sm font-medium md:flex lg:gap-8">
              <Link to="/landing#notice-board" className="text-gray-600 transition hover:text-brand">
                Announcement
              </Link>
              <Link to="/landing#workspace" className="text-gray-600 transition hover:text-brand">
                Workspace
              </Link>
              <Link
                to={isAuthenticated ? dashboardPath : '/login'}
                className="text-sm font-semibold text-gray-700 transition hover:text-brand"
              >
                {isAuthenticated ? 'Dashboard' : 'Login'}
              </Link>
            </nav>

            <div className="flex shrink-0 items-center gap-2">
              <SocialLinks className="hidden sm:flex" />
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 md:hidden"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              >
                {isMobileMenuOpen ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {!isMobileMenuOpen ? <HeaderAnnouncementTicker /> : null}
        </div>
      </header>

      {isMobileMenuOpen ? (
        <div className="fixed inset-0 z-[60] flex flex-col bg-white md:hidden">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between border-b border-gray-100 px-4 py-3 sm:px-6">
            <Link
              to="/"
              className="flex shrink-0 items-center gap-2 text-brand"
              onClick={closeMobileMenu}
            >
              <img
                src={IMAGES.LOGO}
                alt="Grover Sweets Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100"
              aria-label="Close menu"
              onClick={closeMobileMenu}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <nav className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-6 sm:px-6">
            <div className="flex flex-col gap-1 font-medium">
              <Link
                to="/landing#notice-board"
                className={navLinkClass}
                onClick={closeMobileMenu}
              >
                Announcement
              </Link>
              <Link
                to="/landing#workspace"
                className={navLinkClass}
                onClick={closeMobileMenu}
              >
                Workspace
              </Link>
              <Link
                to={isAuthenticated ? dashboardPath : '/login'}
                className="rounded-lg px-1 py-2.5 text-base font-semibold text-gray-700 transition hover:bg-gray-50 hover:text-brand"
                onClick={closeMobileMenu}
              >
                {isAuthenticated ? 'Dashboard' : 'Login'}
              </Link>
            </div>
            <SocialLinks className="mt-6" />
          </nav>
        </div>
      ) : null}
    </>
  );
}

export default Header;
